import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { transitionSession } from '../services/sessionTransitions';

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export interface WizardState {
  step: WizardStep;
  sessionId: string | null;
  clientId: string | null;
  actType: string | null;
  contractId: string | null;
  quoteId: string | null;
  checklist: Record<string, boolean>;
}

export const DEFAULT_CHECKLIST_KEYS = [
  'contraindications_checked',
  'allergies_checked',
  'treatments_checked',
  'risks_explained',
  'postcare_explained',
  'new_needles_confirmed',
  'workspace_disinfected',
  'gloves_on',
] as const;

const INITIAL_CHECKLIST = Object.fromEntries(
  DEFAULT_CHECKLIST_KEYS.map((k) => [k, false])
) as Record<string, boolean>;

export type SessionConflict = { conflict: true; sessionId: string; status: string; clientName: string };

export function useSessionWizard(initialClientId?: string, initialQuoteId?: string) {
  const navigate = useNavigate();

  const [state, setState] = useState<WizardState>({
    step: initialClientId ? 2 : 1,
    sessionId: null,
    clientId: initialClientId ?? null,
    actType: null,
    contractId: null,
    quoteId: initialQuoteId ?? null,
    checklist: { ...INITIAL_CHECKLIST },
  });

  const selectClient = (clientId: string) => {
    setState((s) => ({ ...s, clientId, step: 2 }));
  };

  const createDraftSession = async (actType: string): Promise<string | SessionConflict> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!state.clientId) throw new Error('No client selected');

    const { data: clientData } = await (supabase as any)
      .from('clients')
      .select('name, first_name, last_name')
      .eq('id', state.clientId)
      .single();

    const clientName = (clientData as any)?.name
      || `${(clientData as any)?.first_name || ''} ${(clientData as any)?.last_name || ''}`.trim()
      || '';

    // Block only if client already has an active session of the SAME act_type
    const { data: active } = await (supabase as any)
      .from('sessions')
      .select('id, status_v2')
      .eq('owner_id', user.id)
      .eq('client_id', state.clientId)
      .eq('act_type', actType)
      .in('status_v2', ['in_progress', 'paused'])
      .limit(1)
      .maybeSingle();

    if (active) {
      return { conflict: true, sessionId: active.id, status: active.status_v2, clientName };
    }

    const { data, error } = await supabase
      .from('sessions')
      .insert({
        owner_id: user.id,
        client_id: state.clientId,
        act_type: actType,
        status_v2: 'draft' as any,
        status: 'draft',
        client_name: clientName,
      } as any)
      .select('id')
      .single();

    if (error) throw new Error(`Failed to create session: ${error.message}`);
    setState((s) => ({ ...s, sessionId: data.id, actType }));
    return data.id;
  };

  const advanceToContract = async (actType: string): Promise<SessionConflict | void> => {
    const result = await createDraftSession(actType);
    if (typeof result !== 'string') return result;
    await transitionSession(result, 'contract_pending');
    setState((s) => ({ ...s, step: 3 }));
  };

  const markContractSigned = async (contractId: string): Promise<void> => {
    if (!state.sessionId) throw new Error('No session');
    await transitionSession(state.sessionId, 'contract_signed', {
      attached_contract_id: contractId,
    });
    setState((s) => ({ ...s, contractId, step: 4 }));
  };

  const skipContract = async (): Promise<void> => {
    if (!state.sessionId) throw new Error('No session');
    await transitionSession(state.sessionId, 'contract_signed');
    setState((s) => ({ ...s, step: 4 }));
  };

  const updateChecklist = (key: string, value: boolean): void => {
    setState((s) => ({
      ...s,
      checklist: { ...s.checklist, [key]: value },
    }));
  };

  const isChecklistComplete = (): boolean =>
    DEFAULT_CHECKLIST_KEYS.every((k) => state.checklist[k]);

  // Saves checklist to DB, transitions to checklist_pending, advances to Step 5.
  // Does NOT start the session yet — that happens in launchSession().
  const advanceToMaterials = async (): Promise<void> => {
    if (!state.sessionId) throw new Error('No session');
    if (!isChecklistComplete()) throw new Error('Checklist incomplete');

    await supabase
      .from('sessions')
      .update({ checklist: state.checklist } as any)
      .eq('id', state.sessionId);

    await transitionSession(state.sessionId, 'checklist_pending');
    setState((s) => ({ ...s, step: 5 }));
  };

  // Finalizes the wizard: saves project info, inserts stock reservations,
  // transitions session to in_progress, navigates to /sessions/:id.
  const launchSession = async (data: {
    projectName: string;
    bodyZone: string;
    style: string;
    projectNotes: string;
    materials: Array<{ productId: string; quantity: number }>;
  }): Promise<void> => {
    if (!state.sessionId) throw new Error('No session');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    await supabase
      .from('sessions')
      .update({
        project_name:  data.projectName  || null,
        body_zone:     data.bodyZone     || null,
        style:         data.style        || null,
        project_notes: data.projectNotes || null,
      } as any)
      .eq('id', state.sessionId);

    if (data.materials.length > 0) {
      await (supabase as any).from('stock_movements').insert(
        data.materials.map((m) => ({
          owner_id:      user.id,
          session_id:    state.sessionId,
          product_id:    m.productId,
          quantity:      m.quantity,
          movement_type: 'reserved',
        }))
      );
    }

    await transitionSession(state.sessionId, 'in_progress');
    navigate(`/sessions/${state.sessionId}`);
  };

  // Called by SessionWizard when returning from ContractForm with ?sessionId=&contractId=
  const resumeAfterContract = async (sessionId: string, contractId: string): Promise<void> => {
    const { data: session, error } = await (supabase as any)
      .from('sessions')
      .select('id, client_id, act_type, status_v2')
      .eq('id', sessionId)
      .single();

    if (error || !session) throw new Error('Session introuvable');

    if (session.status_v2 === 'contract_pending') {
      await transitionSession(sessionId, 'contract_signed', {
        attached_contract_id: contractId,
      });
    }

    setState({
      step: 4,
      sessionId,
      clientId:   session.client_id ?? null,
      actType:    session.act_type  ?? null,
      contractId,
      quoteId:    null,
      checklist:  { ...INITIAL_CHECKLIST },
    });
  };

  return {
    state,
    setState,
    selectClient,
    advanceToContract,
    markContractSigned,
    skipContract,
    updateChecklist,
    isChecklistComplete,
    advanceToMaterials,
    launchSession,
    resumeAfterContract,
    DEFAULT_CHECKLIST_KEYS,
  };
}
